import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDaycarePackages } from '../hooks/useDaycarePackages';
import { filterPackagesBySearch, sortPackages, PACKAGE_TYPES, getPackageStatus, getPackageWarnings } from '../utils/daycareUtils';
import { paginateArray } from '../utils/dataUtils';
import { DogService } from '../services/dogService';
import { UI_CONFIG } from '../constants/config';
import Navigation from '../components/Navigation';
import '../styles/pages/daycare.css';

const toDateInput = (val) => {
    if (!val) return '';
    return new Date(val).toISOString().split('T')[0];
};

const toDisplayDate = (val) => {
    if (!val) return '';
    const [y, m, d] = new Date(val).toISOString().split('T')[0].split('-');
    return `${d}-${m}-${y}`;
};

const fromDateInput = (str) => {
    if (!str) return null;
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d, 12, 0, 0).toISOString();
};

export default function DaycarePackages() {
    const { user, authorizedFetch } = useAuth();
    const {
        packages,
        loading,
        error,
        success,
        createPackage,
        updatePackage,
        deletePackage,
        cancelPackage,
        clearMessages
    } = useDaycarePackages();

    // Local state
    const [dogs, setDogs] = useState([]);
    const [dogLoadError, setDogLoadError] = useState('');
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState('dogName');
    const [sortDirection, setSortDirection] = useState('asc');
    
    // Form state
    const [formData, setFormData] = useState({
        dogId: '',
        packageType: '1-day'
    });
    const [dogSearch, setDogSearch] = useState('');
    const [managingDogsFor, setManagingDogsFor] = useState(null); // packageId
    const [addDogSearch, setAddDogSearch] = useState('');

    // Edit state
    const [editingPackage, setEditingPackage] = useState(null); // pkg._id
    const [editForm, setEditForm] = useState({
        packageType: '', daysLeft: 0,
        purchasedAt: '', firstUsedAt: '', expiresAt: ''
    });

    // Load dogs for dropdown
    useEffect(() => {
        if (!user) return;

        const loadDogs = async () => {
            try {
                const response = await DogService.getAllDogs(authorizedFetch, { limit: 1000 });
                const dogsData = response.data ? response.data : (Array.isArray(response) ? response : []);
                setDogs(dogsData);
            } catch (err) {
                console.error('Failed to load dogs:', err);
                setDogLoadError(err.message || 'Error al cargar los perros');
            }
        };

        loadDogs();
    }, [user, authorizedFetch]);

    // Computed values
    const filteredPackages = filterPackagesBySearch(packages, search);
    const sortedPackages = sortPackages(filteredPackages, sortBy, sortDirection);
    const paginationResult = paginateArray(
        sortedPackages,
        currentPage,
        UI_CONFIG.PAGINATION.BOOKINGS_PER_PAGE
    );

    // Event handlers
    const handleFormChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        clearMessages();

        try {
            await createPackage(formData);
            // Reset form on success
            setFormData({ dogId: '', packageType: '1-day' });
        } catch (err) {
            // Error is handled in the hook
        }
    };

    const handleDelete = async (packageId) => {
        if (!window.confirm('¿Eliminar permanentemente este paquete? Esta acción no se puede deshacer.')) return;
        clearMessages();
        try {
            await deletePackage(packageId);
        } catch (err) {
            // error is set by the hook
        }
    };

    const handleCancel = async (packageId) => {
        if (!window.confirm('¿Cancelar este paquete? Será marcado como cancelado y eliminado de los paquetes activos.')) return;
        clearMessages();
        try {
            await cancelPackage(packageId);
        } catch (err) {
            // error is set by the hook
        }
    };

    const handleSearchChange = (searchTerm) => {
        setSearch(searchTerm);
        setCurrentPage(1);
    };

/*    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortDirection('asc');
        }
        setCurrentPage(1);
    };
*/
    const handleAddSharedDog = async (pkg, dogId) => {
        if (dogId === pkg.dog?._id) return;
        const current = (pkg.sharedDogs || []).map(d => d._id || d);
        if (current.includes(dogId)) return;
        clearMessages();
        try {
            await updatePackage(pkg._id, { sharedDogs: [...current, dogId] });
            setAddDogSearch('');
        } catch (err) {}
    };

    const handleRemoveSharedDog = async (pkg, dogId) => {
        const current = (pkg.sharedDogs || []).map(d => d._id || d).filter(id => id !== dogId);
        clearMessages();
        try {
            await updatePackage(pkg._id, { sharedDogs: current });
        } catch (err) {}
    };

    const handleEdit = (pkg) => {
        setEditingPackage(pkg._id);
        setEditForm({
            packageType: pkg.packageType,
            daysLeft: pkg.daysLeft,
            purchasedAt: toDateInput(pkg.purchasedAt),
            firstUsedAt: toDateInput(pkg.firstUsedAt),
            expiresAt:   toDateInput(pkg.expiresAt),
        });
        setManagingDogsFor(null);
    };

    const handleEditSave = async () => {
        clearMessages();
        try {
            await updatePackage(editingPackage, {
                packageType: editForm.packageType,
                daysLeft:    editForm.daysLeft,
                purchasedAt: fromDateInput(editForm.purchasedAt),
                firstUsedAt: fromDateInput(editForm.firstUsedAt),
                expiresAt:   fromDateInput(editForm.expiresAt),
            });
            setEditingPackage(null);
        } catch (err) {}
    };

    const handleEditCancel = () => setEditingPackage(null);

    return (
        <div>
            <Navigation />
            <div className="daycare-container">

            {/* Header */}
            <div className="daycare-header">
                <h1>Paquetes de Guardería</h1>
                <Link to="/daycare/packages/history" className="daycare-link" style={{ fontSize: '0.85rem', fontWeight: 700 }}>
                    📋 Historial
                </Link>
            </div>

            {/* Messages */}
            {(error || dogLoadError) && <div className="error-message">{error || dogLoadError}</div>}
            {success && <div className="success-message">{success}</div>}

            {/* Purchase Form */}
            <div className="daycare-form">
                <h2>Asignar Paquete de Guardería</h2>
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Perro</label>
                        <input
                            type="text"
                            className="dog-picker-search"
                            placeholder="Buscar perro..."
                            value={dogSearch}
                            onChange={e => setDogSearch(e.target.value)}
                        />
                        <div className="dog-picker-list">
                            {dogs
                                .filter(dog => `${dog.name} ${dog.breed}`.toLowerCase().includes(dogSearch.toLowerCase()))
                                .map(dog => {
                                    const isSelected = formData.dogId === dog._id;
                                    return (
                                        <button
                                            type="button"
                                            key={dog._id}
                                            className={`dog-picker-card${isSelected ? ' dog-picker-card--selected' : ''}`}
                                            onClick={() => handleFormChange('dogId', dog._id)}
                                        >
                                            <div className="dog-picker-avatar">
                                                {dog.pictureUrl || dog.picture
                                                    ? <img src={dog.pictureUrl || dog.picture} alt={dog.name} />
                                                    : <span>{dog.name?.[0]?.toUpperCase()}</span>
                                                }
                                            </div>
                                            <div className="dog-picker-info">
                                                <div className="dog-picker-name">{dog.name}</div>
                                                <div className="dog-picker-breed">{dog.breed}</div>
                                            </div>
                                            {isSelected && <span className="dog-picker-check">✓</span>}
                                        </button>
                                    );
                                })
                            }
                            {dogs.filter(dog => `${dog.name} ${dog.breed}`.toLowerCase().includes(dogSearch.toLowerCase())).length === 0 && (
                                <div className="dog-picker-empty">No se encontraron perros</div>
                            )}
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Tipo de paquete</label>
                        <select
                            value={formData.packageType}
                            onChange={e => handleFormChange('packageType', e.target.value)}
                        >
                            {Object.entries(PACKAGE_TYPES).map(([value, config]) => (
                                <option key={value} value={value}>
                                    {config.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <button type="submit" disabled={loading}>
                        {loading ? 'Procesando...' : 'Adquirir paquete'}
                    </button>
                </form>
            </div>

            {/* Navigation Cards */}
            <div className="daycare-list">
                <Link to="/daycare/packages/active" className="daycare-card">
                    <h3>Paquetes activos</h3>
                    <p>{packages.length} activos</p>
                </Link>
                <Link to="/daycare/" className="daycare-card">
                    <h3>Gestión diaria</h3>
                    <p>Entradas, salidas y registros diarios</p>
                </Link>
            </div>

            {/* Active Packages Section */}
            <div className="packages-section">
                <div className="section-header">
                    <h2>Paquetes activos</h2>

                    {/* Search and Sort Controls */}
                    <div className="controls">
                        <input
                            type="text"
                            placeholder="Buscar por nombre del perro o tipo de paquete"
                            value={search}
                            onChange={e => handleSearchChange(e.target.value)}
                            className="search-input"
                        />
                        <select
                            value={`${sortBy}-${sortDirection}`}
                            onChange={e => {
                                const [field, direction] = e.target.value.split('-');
                                setSortBy(field);
                                setSortDirection(direction);
                                setCurrentPage(1);
                            }}
                            className="sort-select"
                        >
                            <option value="dogName-asc">Nombre del perro (A-Z)</option>
                            <option value="dogName-desc">Nombre del perro (Z-A)</option>
                            <option value="packageType-asc">Tipo de paquete (A-Z)</option>
                            <option value="packageType-desc">Tipo de paquete (Z-A)</option>
                            <option value="daysLeft-asc">Días restantes (menos primero)</option>
                            <option value="daysLeft-desc">Días restantes (más primero)</option>
                            <option value="expiresAt-asc">Vence pronto primero</option>
                            <option value="expiresAt-desc">Vence después primero</option>
                        </select>
                    </div>
                </div>

                {/* Packages List */}
                {loading ? (
                    <div className="text-center p-4">Cargando paquetes...</div>
                ) : paginationResult.items.length === 0 ? (
                    <div className="text-center p-4">
                        {search ? 'No se encontraron paquetes que coincidan con tu búsqueda.' : 'No hay paquetes activos.'}
                    </div>
                ) : (
                    <>
                        <div className="packages-stats mb-4">
                            Mostrando {paginationResult.items.length} de {paginationResult.totalItems} paquetes
                        </div>
                        <div className="daycare-list">
                            {paginationResult.items.map(pkg => {
                                const status = getPackageStatus(pkg.daysLeft);
                                const pkgWarnings = getPackageWarnings(pkg);
                                return (
                                    <div key={pkg._id} className={`daycare-card package-${status.type}`}>
                                        <div className="package-header">
                                            <div>
                                                <h3>{pkg.dog?.name || 'Unknown Dog'}</h3>
                                                {pkg.dog?.breed && (
                                                    <div className="package-dog-breed">{pkg.dog.breed}</div>
                                                )}
                                            </div>
                                            <span className={`status-badge ${status.type}`}>
                                                {status.label}
                                            </span>
                                        </div>

                                        {pkgWarnings.length > 0 && (
                                            <div className="pkg-warnings">
                                                {pkgWarnings.map((w, i) => (
                                                    <div key={i} className={`pkg-warning pkg-warning--${w.type}`}>
                                                        ⚠️ {w.message}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Shared dogs */}
                                        {(pkg.sharedDogs?.length > 0) && (
                                            <div className="pkg-shared-dogs">
                                                <span className="pkg-shared-label">Compartido con:</span>
                                                <div className="pkg-shared-list">
                                                    {pkg.sharedDogs.map(sd => (
                                                        <span key={sd._id} className="pkg-shared-chip">
                                                            {sd.name}
                                                            <button
                                                                className="pkg-shared-remove"
                                                                onClick={() => handleRemoveSharedDog(pkg, sd._id)}
                                                                title="Quitar perro"
                                                            >×</button>
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <div className="package-details">
                                            <p><strong>Paquete:</strong> {PACKAGE_TYPES[pkg.packageType]?.label || pkg.packageType}</p>
                                            <p><strong>Días restantes:</strong> {pkg.daysLeft}</p>
                                            <p><strong>Vence:</strong> {pkg.expiresAt ? toDisplayDate(pkg.expiresAt) : 'Se establece en primer uso'}</p>
                                        </div>

                                        {/* Inline edit panel */}
                                        {editingPackage === pkg._id && (
                                            <div className="pkg-edit-panel">
                                                <div className="pkg-edit-row">
                                                    <label>Tipo</label>
                                                    <select value={editForm.packageType} onChange={e => setEditForm(f => ({...f, packageType: e.target.value}))}>
                                                        <option value="1-day">1 Día</option>
                                                        <option value="15-day">15 Días</option>
                                                        <option value="30-day">30 Días</option>
                                                    </select>
                                                </div>
                                                <div className="pkg-edit-row">
                                                    <label>Días restantes</label>
                                                    <input type="number" min="0" value={editForm.daysLeft}
                                                        onChange={e => setEditForm(f => ({...f, daysLeft: parseInt(e.target.value) || 0}))} />
                                                </div>
                                                <div className="pkg-edit-row">
                                                    <label>Fecha de compra</label>
                                                    <input type="date" value={editForm.purchasedAt}
                                                        onChange={e => setEditForm(f => ({...f, purchasedAt: e.target.value}))} />
                                                </div>
                                                <div className="pkg-edit-row">
                                                    <label>Inicio de vigencia</label>
                                                    <input type="date" value={editForm.firstUsedAt}
                                                        onChange={e => setEditForm(f => ({...f, firstUsedAt: e.target.value}))} />
                                                </div>
                                                <div className="pkg-edit-row">
                                                    <label>Vencimiento</label>
                                                    <input type="date" value={editForm.expiresAt}
                                                        onChange={e => setEditForm(f => ({...f, expiresAt: e.target.value}))} />
                                                </div>
                                                <div className="pkg-edit-actions">
                                                    <button className="pkg-edit-save" onClick={handleEditSave} disabled={loading}>Guardar</button>
                                                    <button className="pkg-edit-cancel" onClick={handleEditCancel}>Cancelar</button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Manage shared dogs panel */}
                                        {managingDogsFor === pkg._id ? (
                                            <div className="pkg-add-dog-panel">
                                                <input
                                                    type="text"
                                                    placeholder="Buscar perro para agregar..."
                                                    value={addDogSearch}
                                                    onChange={e => setAddDogSearch(e.target.value)}
                                                    className="pkg-add-dog-search"
                                                    autoFocus
                                                />
                                                <div className="pkg-add-dog-list">
                                                    {dogs
                                                        .filter(d => {
                                                            if (d._id === pkg.dog?._id) return false;
                                                            if ((pkg.sharedDogs || []).some(s => s._id === d._id)) return false;
                                                            if (!addDogSearch) return true;
                                                            return `${d.name} ${d.breed}`.toLowerCase().includes(addDogSearch.toLowerCase());
                                                        })
                                                        .slice(0, 6)
                                                        .map(d => (
                                                            <button
                                                                key={d._id}
                                                                className="pkg-add-dog-item"
                                                                onClick={() => handleAddSharedDog(pkg, d._id)}
                                                            >
                                                                <span className="pkg-add-dog-name">{d.name}</span>
                                                                <span className="pkg-add-dog-breed">{d.breed}</span>
                                                            </button>
                                                        ))
                                                    }
                                                </div>
                                                <button
                                                    className="pkg-add-dog-close"
                                                    onClick={() => { setManagingDogsFor(null); setAddDogSearch(''); }}
                                                >
                                                    Cerrar
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                className="pkg-share-btn"
                                                onClick={() => { setManagingDogsFor(pkg._id); setAddDogSearch(''); }}
                                            >
                                                + Agregar perro compartido
                                            </button>
                                        )}

                                        <div className="actions">
                                            <button
                                                className="edit-btn"
                                                onClick={() => editingPackage === pkg._id ? handleEditCancel() : handleEdit(pkg)}
                                                disabled={loading}
                                                style={{ background: '#3b82f6', color: 'white' }}
                                            >
                                                {editingPackage === pkg._id ? 'Cerrar' : 'Editar'}
                                            </button>
                                            <button
                                                className="edit-btn"
                                                onClick={() => handleCancel(pkg._id)}
                                                disabled={loading}
                                                style={{ background: '#fef3c7', color: '#92400e' }}
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                className="delete-btn"
                                                onClick={() => handleDelete(pkg._id)}
                                                disabled={loading}
                                            >
                                                Eliminar
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* Pagination */}
                {paginationResult.totalPages > 1 && (
                    <div className="pagination mt-4">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={!paginationResult.hasPrevPage}
                        >
                            Anterior
                        </button>
                        <span className="mx-4">
                            Página {paginationResult.currentPage} de {paginationResult.totalPages}
                        </span>
                        <button
                            onClick={() => setCurrentPage(prev => Math.min(paginationResult.totalPages, prev + 1))}
                            disabled={!paginationResult.hasNextPage}
                        >
                            Siguiente
                        </button>
                    </div>
                )}
            </div>
            </div>
        </div>
    );
}
